import {
  FRONT_DOOR_FAMILY_ROLES,
  FRONT_DOOR_ONLY_PARAMS,
} from "@/lib/modules/auth/front-door/constants";
import type {
  FrontDoorRole,
  FrontDoorSignUpRole,
} from "@/lib/modules/auth/front-door/types";
import { ReferralSource } from "@prisma/client";
import {
  isPediatricianSchool,
  NYC_HEALTH_AND_HOSPITALS_SCHOOL_ID,
} from "@/lib/modules/referrals/pediatricianSchools/utils";

export const isFamilyFrontDoorRole = (role: FrontDoorRole) =>
  FRONT_DOOR_FAMILY_ROLES.includes(role);

export const isFrontDoorSignUpRole = (
  role: FrontDoorRole,
): role is FrontDoorSignUpRole =>
  role === "SCHOOL_WORKER" || role === "PEDIATRICIAN" || role === "HEALTH_PLAN";

/**
 * The pill picked when the page opens. The old sign-up links still work: a
 * pediatric school link opens Pediatrician, and the health plan link opens the
 * health plan pill.
 */
export const getInitialFrontDoorRole = ({
  schoolId,
  isInsurancePayer,
}: {
  schoolId?: number;
  isInsurancePayer?: boolean;
}): FrontDoorRole => {
  if (isInsurancePayer) {
    return "HEALTH_PLAN";
  }
  if (isPediatricianSchool(schoolId)) {
    return "PEDIATRICIAN";
  }
  return "SCHOOL_WORKER";
};

/**
 * The school a front-door sign-up joins. A school link only counts for the
 * role it was made for, and pediatricians without one join NYC Health +
 * Hospitals, as the home page's Pediatrician link does. Health plan staff
 * pick their plan during onboarding, so they join none here.
 */
export const getFrontDoorSignUpSchoolId = ({
  role,
  schoolId,
}: {
  role: FrontDoorSignUpRole;
  schoolId?: number;
}): number | undefined => {
  if (role === "HEALTH_PLAN") {
    return undefined;
  }
  if (role === "PEDIATRICIAN") {
    return isPediatricianSchool(schoolId)
      ? schoolId
      : NYC_HEALTH_AND_HOSPITALS_SCHOOL_ID;
  }
  return schoolId && !isPediatricianSchool(schoolId) ? schoolId : undefined;
};

/** Only same-site paths, so a crafted link can't send someone off Marble. */
export const getSafeCallbackUrl = (callbackUrl?: string | null) =>
  callbackUrl && callbackUrl.startsWith("/") && !callbackUrl.startsWith("//")
    ? callbackUrl
    : undefined;

/**
 * Where a family's Refer button goes: the referral form for them, carrying
 * the link's own params (UTM tags, `schoolId`) but not the front door's.
 */
export const getFrontDoorReferHref = ({
  isSelf,
  searchParams,
}: {
  isSelf: boolean;
  searchParams: URLSearchParams;
}): string => {
  const params = new URLSearchParams({
    source: isSelf ? ReferralSource.SELF : ReferralSource.GUARDIAN,
  });
  searchParams.forEach((value, key) => {
    if (key !== "source" && !FRONT_DOOR_ONLY_PARAMS.includes(key)) {
      params.append(key, value);
    }
  });
  return `/refer?${params.toString()}`;
};
